// @ts-nocheck
// TODO: tighten types after migration
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FloatingBubbles from "./FloatingBubbles";
import SponsorsCarousel from "./SponsorsCarousel";
import "./Home.css";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { robustFetch } from "../utils/apiUtils";
import SEO from "../Blog/SEO";
import { API_ENDPOINTS } from "../config/api";
import PodcastDisplay from "./PodcastDisplay";
import LogoLoader from "../components/LogoLoader";
import { getBlogPostPath } from "../utils/blogRoutes";



function formatDate(dateStr) {
  try {
    if (!dateStr) return "Recently";
    const published = new Date(dateStr);
    // Check if date is valid
    if (isNaN(published.getTime())) {
      return "Recently";
    }
    const now = new Date();
    const diffMs = now - published;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffHours < 24) {
      if (diffMinutes < 1) return "just now";
      if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes===1?"":"s"} ago`;
      return `${diffHours} hour${diffHours===1?"":"s"} ago`;
    }
    const day = published.getDate();
    const monthStr = published.toLocaleString("default", { month: "short" });
    // Ensure first letter is uppercase and rest are lowercase
    const month = monthStr.charAt(0).toUpperCase() + monthStr.slice(1).toLowerCase();
    const year = published.getFullYear();
    return `${day} ${month} ${year}`;
  } catch (_) {
    return "Recently";
  }
}

function HomeBlogList() {
  // ========== MONGODB POSTS FETCHING START ==========
  const [mongoPosts, setMongoPosts] = useState([]);
  const [mongoLoading, setMongoLoading] = useState(true);
  const [mongoError, setMongoError] = useState(null);

  // Fetch posts from MongoDB BlogDb
  useEffect(() => {
    // Load cached posts immediately so the UI shows without waiting for the API
    let hasCachedData = false;
    try {
      const cached = localStorage.getItem('home_blog_posts_v1') || sessionStorage.getItem('home_blog_posts_v1');
      if (cached) {
        const cachedPosts = JSON.parse(cached);
        if (Array.isArray(cachedPosts) && cachedPosts.length > 0) {
          setMongoPosts(cachedPosts);
          setMongoLoading(false);
          hasCachedData = true;
        }
      }
    } catch(e) {}

    const fetchMongoPosts = async (retryCount = 0) => {
      const controller = new AbortController();
      try {
        if (!hasCachedData) setMongoLoading(true);
        setMongoError(null);
        
        const response = await robustFetch(API_ENDPOINTS.BLOG.POSTS, { signal: controller.signal });
        
        if (!response.ok) {
          if (response.status === 404 || response.status === 503) {
            if (!hasCachedData) setMongoPosts([]);
            setMongoLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        let postsData;
        try {
          postsData = await response.json();
        } catch (parseErr) {
          if (!hasCachedData) setMongoPosts([]);
          setMongoLoading(false);
          return;
        }

        if (postsData && Array.isArray(postsData)) {
          // Filter only published posts
          const publishedPosts = postsData.filter(post => post.isPublished === true);
          
          if (publishedPosts.length > 0) {
            // Convert MongoDB posts to unified format
            const formattedPosts = publishedPosts.map(post => {
              // Handle category
              let categories = [];
              if (post.category) {
                const categoryArray = Array.isArray(post.category) ? post.category : [post.category];
                categories = categoryArray.map(cat => ({
                  name: cat.name || 'Uncategorized',
                  slug: cat.slug || (cat.name ? cat.name.toLowerCase().replace(/\s+/g, '-') : 'uncategorized')
                }));
              }
              
              // Handle coverImage
              let coverImage = { url: '' };
              if (post.coverImage) {
                if (typeof post.coverImage === 'string') {
                  coverImage = { url: post.coverImage };
                } else if (post.coverImage.url) {
                  coverImage = { url: post.coverImage.url };
                }
              }
              
              return {
                id: post._id || post.id,
                slug: post.slug,
                title: post.title,
                postNumber: post.postNumber || 0,
                createdAt: post.createdAt,
                coverImage: coverImage,
                categories: categories
              };
            });
            
            // Sort MongoDB posts by postNumber (descending) before setting
            formattedPosts.sort((a, b) => {
              const postNumA = a.postNumber || 0;
              const postNumB = b.postNumber || 0;
              return postNumB - postNumA; // Descending order (highest first)
            });

            // Cache for instant display on next visit
            try { 
              sessionStorage.setItem('home_blog_posts_v1', JSON.stringify(formattedPosts)); 
              localStorage.setItem('home_blog_posts_v1', JSON.stringify(formattedPosts)); 
            } catch(e) {}

            setMongoPosts(formattedPosts);
          } else if (!hasCachedData) {
            setMongoPosts([]);
          }
        } else if (!hasCachedData) {
          setMongoPosts([]);
        }
        setMongoLoading(false);
      } catch (err) {
        console.error(`Error fetching MongoDB posts (Attempt ${retryCount + 1}):`, err);
        
        if (retryCount < 4) { // Retry up to 5 attempts to give Render cold-start time
            console.log("Retrying fetch...");
            setTimeout(() => fetchMongoPosts(retryCount + 1), 2000); 
        } else {
            if (!hasCachedData) {
              setMongoError(err);
              setMongoPosts([]);
            }
            setMongoLoading(false);
        }
      }
    };

    fetchMongoPosts();
  }, []);
  // ========== MONGODB POSTS FETCHING END ==========

  // Consolidate posts (only MongoDB now)
  const posts = useMemo(() => {
    let allPosts = [];
    
    // Add MongoDB posts
    if (mongoPosts.length > 0) {
      allPosts.push(...mongoPosts);
    }
    
    // Sort by postNumber (highest number = latest post)
    allPosts.sort((a, b) => {
      const postNumA = a.postNumber || 0;
      const postNumB = b.postNumber || 0;
      return postNumB - postNumA; // Descending order (highest first)
    });
    
    // Take only the latest 3 posts (highest post numbers)
    return allPosts.slice(0, 3);
  }, [mongoPosts]);

  if (mongoLoading && posts.length === 0) {
    return (
      <div className="home-blog-list flex w-full flex-col px-[0.5rem]">
        {[1, 2, 3].map((item) => (
          <React.Fragment key={item}>
            <div className="home-blog-item flex items-start justify-center py-[0.5rem] text-inherit no-underline transition-colors duration-200">
              <div className="skeleton skeleton-badge"></div>
              <div className="skeleton skeleton-thumbnail"></div>
              <div className="skeleton-info">
                <div className="skeleton skeleton-title"></div>
                <div className="skeleton skeleton-date"></div>
              </div>
            </div>
            {item < 3 && <div className="home-blog-separator mx-auto h-[2px] w-[95%] bg-black/10"></div>}
          </React.Fragment>
        ))}
      </div>
    );
  }
  
  // Handle errors gracefully
  if (mongoError) {
    if (posts.length === 0 && !mongoLoading) {
      return <div className="error-message mx-[20px] my-0 flex h-[200px] items-center justify-center rounded-[15px] border-2 border-[#fecaca] bg-bg p-[2rem] text-center text-[1.2rem] text-[#dc2626]">Unable to load latest posts. Please try again later.</div>;
    }
  }
  
  if (!posts.length && !mongoLoading) {
    return <div className="no-posts mx-[20px] my-0 flex h-[200px] items-center justify-center rounded-[15px] bg-bg p-[2rem] text-center text-[1.2rem] text-[#666]">No posts available</div>;
  }

  return (
    <div className="home-blog-list flex w-full flex-col px-[0.5rem]">
      {posts.map((post, idx) => {
         const thumbnail = post.coverImage && post.coverImage.url ? post.coverImage.url : "/images/home_images/Logo.png";

         return (
            <React.Fragment key={post.id}>
                <NavLink 
                  to={getBlogPostPath(post)}
                    className="home-blog-item flex items-start justify-center py-[0.5rem] text-inherit no-underline transition-colors duration-200 hover:rounded-[15px] hover:bg-[rgba(255,209,25,0.855)]"
                >
                    <div className="home-blog-thumbnail-wrapper mr-[1rem] w-[90px] shrink-0 overflow-hidden rounded-[8px] bg-[#333] [aspect-ratio:4/3] max-[480px]:w-[70px] min-[481px]:max-[767px]:w-[80px] min-[768px]:max-[1024px]:w-[90px]">
                        <img
                            src={thumbnail}
                            alt={post.title}
                            className="home-blog-thumbnail block h-full w-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.target.src = "/images/home_images/Logo.png"; }}
                        />
                    </div>
                    <div className="home-blog-info flex flex-1 flex-col justify-start gap-[0.25rem]">
                        <div className="home-blog-meta flex w-full items-center justify-between">
                            <span className="home-blog-number text-[0.85rem] font-[600] uppercase tracking-[0.5px] text-black/60 max-[480px]:text-[0.75rem] min-[768px]:max-[1024px]:text-[0.85rem]">#{post.postNumber || 0}</span>
                            <span className="home-blog-date text-[0.85rem] font-[600] uppercase tracking-[0.5px] text-black/60 max-[480px]:text-[0.75rem] min-[768px]:max-[1024px]:text-[0.85rem]">{formatDate(post.createdAt)}</span>
                        </div>
                        <h3 className="home-blog-post-title m-0 text-left text-[1.1rem] font-[700] leading-[1.3] text-[#111] max-[480px]:text-[0.95rem] min-[768px]:max-[1024px]:text-[1.1rem]">{post.title}</h3>
                    </div>
                </NavLink>
                {idx < posts.length - 1 && <div className="home-blog-separator mx-auto h-[2px] w-[95%] bg-black/10"></div>}
            </React.Fragment>
         );
      })}
    </div>
  );
}

const BLOG_SECTIONS = [
  { text: "Reading settings", desc: "Adjust font size, theme and reading preferences", image: "https://home-asset.s3.eu-north-1.amazonaws.com/HomeBlog+Image+1.png" },
  { text: "All device compatible", desc: "Read seamlessly on desktop, tablet and mobile", image: "https://home-asset.s3.eu-north-1.amazonaws.com/HomeBlog+Image+2.png" },
  { text: "No ads and no popups", desc: "A clean, distraction-free reading experience", image: "https://home-asset.s3.eu-north-1.amazonaws.com/HomeBlog+Image+3.png" },
];

export default function Home(){
  // Removed blocking window.load check. Component should render immediately.
  const [activeSection, setActiveSection] = useState(0);
  const [blogImgLoaded, setBlogImgLoaded] = useState(false);
  const intervalRef = useRef(null);

  // Logo Loader State
  const [showLoader, setShowLoader] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Initial loading effect
  useEffect(() => {
    let progress = 0;
    const interval = setInterval(() => {
      // Home page is complex, so let's allow it to finish loading everything
      if (progress < 100) {
        const increment = Math.random() * 15;
        progress = Math.min(progress + increment, 100);
        setLoadingProgress(progress);
      } else {
        clearInterval(interval);
        setTimeout(() => setShowLoader(false), 500);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const startCycle = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveSection(prev => (prev + 1) % BLOG_SECTIONS.length);
    }, 6000);
  }, []);

  useEffect(() => {
    startCycle();
    return () => clearInterval(intervalRef.current);
  }, [startCycle]);


  if (showLoader) {
    return <LogoLoader progress={loadingProgress} />;
  }

  return (
    <>
      <SEO 
        title="Personal Blog, Projects & Portfolio"
        description="Welcome to Hari Vishnu's official website. Explore insightful blog posts, personal projects, portfolio highlights, and more. Stay updated with the latest from Hari Vishnu."
        url="https://www.harivishnu.in/"
        keywords="Hari Vishnu, personal blog, portfolio, web development, technology, business insights"
      />

      {/* Welcome Section */}
      <section className="welcome-message mx-auto my-[2%] flex max-w-[2000px] flex-row flex-nowrap items-center justify-center gap-[1.5rem] max-[900px]:flex-col max-[900px]:gap-[1rem]">
        <FloatingBubbles />
        <div className="welcome-text-box m-[2%] flex h-fit flex-[1_1_200px] flex-col items-center justify-center rounded-[25px] bg-bg text-center max-[900px]:h-auto max-[900px]:w-[96%] max-[900px]:m-[3%_2%]" role="region" aria-label="Welcome message">
          <h1 className="welcome-heading m-[1%_auto] text-[4rem] font-[700] text-post-heading max-[900px]:text-[1.9rem]">Hey Friends <span role="img" aria-label="waving hand">👋</span></h1>
          <p className="welcome-subtext m-[1%_5%] text-[2rem] text-post-content max-[900px]:text-[1.1rem]">
            I’m Hari Vishnu, exploring tech, business, and the real-life journey of turning a startup dream into reality.
          </p>
        </div>
      </section>

      {/* Sponsors Section */}
      <SponsorsCarousel />

      {/* Blog Highlight Section */}
      <section className="home-blog-section mx-auto my-[2%] box-border w-[90%] max-w-[2000px] overflow-hidden rounded-[40px] bg-[#FFDB46] px-[2rem] py-[5rem] text-text max-[1024px]:px-[1rem] max-[1024px]:py-[2rem]">
        <div className="home-blog-container mx-auto flex items-stretch gap-[4rem] max-[1024px]:flex-col-reverse max-[1024px]:gap-[2rem]">
          <div className="home-blog-left relative flex flex-1 flex-col items-center justify-center gap-[1.5rem] py-[0.5rem] max-[1024px]:w-full max-[1024px]:text-center">
            <h4 className="home-blog-label relative z-[2] m-0 self-start text-[0.9rem] font-[700] uppercase tracking-[1px] text-black/55 max-[1024px]:self-center">MOST RECENT</h4>
            {/* <RotatingBlogCards /> - Commented out as requested */}
            <HomeBlogList />
            <NavLink to="/blog" className="home-cta-btn flex h-[40px] w-[120px] items-center justify-center rounded-[12px] border-0 bg-primary-cyan text-[1.1rem] font-[700] text-white no-underline transition-all duration-300 hover:-translate-y-[2px] hover:bg-[#2ea3ff] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] max-[480px]:text-[0.95rem] min-[481px]:max-[767px]:text-[1rem]">View All</NavLink>
          </div>
          <div className="home-blog-right relative flex flex-[1.5] flex-row items-center gap-[2rem] text-[#111] max-[1024px]:w-full max-[1024px]:flex-col">
            <div className="blog-feat-left-col flex flex-1 flex-col gap-[1rem] max-[1024px]:contents">
              <h2 className="home-blog-title m-0 mb-[0.25rem] text-[2rem] font-[700] leading-[1] tracking-[-2px] max-[480px]:order-1">Read my Blog...</h2>
              <div className="blog-feat-left flex flex-col max-[480px]:order-3 max-[480px]:h-[120px] max-[480px]:items-center max-[480px]:justify-center max-[480px]:overflow-hidden min-[481px]:max-[767px]:h-[120px] min-[481px]:max-[767px]:items-center min-[481px]:max-[767px]:justify-center min-[481px]:max-[767px]:overflow-hidden min-[768px]:max-[1024px]:order-3 min-[768px]:max-[1024px]:h-[120px] min-[768px]:max-[1024px]:items-center min-[768px]:max-[1024px]:justify-center min-[768px]:max-[1024px]:overflow-hidden">
                <div className="blog-feat-list flex flex-col gap-[0.25rem]">
                  {BLOG_SECTIONS.map((section, index) => (
                    <div
                      key={index}
                      className={`blog-feat-item relative cursor-pointer overflow-hidden rounded-r-[8px] py-[0.8rem] pr-[1rem] pl-[calc(1rem+4px)]${activeSection === index ? ' active' : ''}`}
                      onClick={() => { setActiveSection(index); startCycle(); }}
                    >
                      <span className="blog-feat-item-text block text-[1.25rem] font-[700] text-[#111] opacity-40 transition-opacity duration-300 max-[480px]:text-[1rem] min-[481px]:max-[767px]:text-[1.1rem]">{section.text}</span>
                      <span className="blog-feat-item-desc mt-[0.2rem] block text-[0.88rem] font-[400] text-[#111] opacity-50 transition-opacity duration-300">{section.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="blog-feat-images relative min-h-[280px] flex-[1.2] self-stretch overflow-hidden rounded-[20px] max-[480px]:order-2 max-[480px]:min-h-[200px] max-[480px]:w-full min-[481px]:max-[767px]:min-h-[220px] min-[481px]:max-[767px]:w-full min-[768px]:max-[1024px]:order-2 min-[768px]:max-[1024px]:min-h-[260px] min-[768px]:max-[1024px]:w-full">
              {!blogImgLoaded && <div className="blog-feat-skeleton skeleton absolute inset-0 z-[2] rounded-[20px]" />}
              {BLOG_SECTIONS.map((section, index) => (
                <img
                  key={index}
                  src={section.image}
                  alt={section.text}
                  className={`blog-feat-img pointer-events-none absolute inset-0 h-full w-full origin-center rounded-[20px] object-contain opacity-0 transition-all duration-500 ${activeSection === index ? 'active scale-[1.07] opacity-100' : 'scale-100'}`}
                  onLoad={() => setBlogImgLoaded(true)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Podcast Display Section */}
      <PodcastDisplay />

      {/* Donation Section */}
      <section className="donation-section mx-auto my-[8%] max-w-[1200px] px-[4%]">
        <div className="donation-inner flex flex-row items-center gap-[6%] max-[1024px]:flex-col max-[1024px]:gap-[2.5rem] max-[1024px]:text-center">
          <div className="donation-text flex-1">
            <h3 className="donation-heading m-0 mb-[1rem] text-[2.2rem] font-[800] text-post-heading max-[480px]:text-[1.6rem] min-[481px]:max-[767px]:text-[1.7rem] min-[768px]:max-[1024px]:text-[1.9rem]">Powered by People Like You <span role="img" aria-label="star">🌟</span></h3>
            <p className="donation-desc text-[1.2rem] leading-[1.7] text-post-content max-[480px]:text-[1rem]">
              This blog thrives on curiosity, creativity and your support.<br/>
              If you've found something here helpful or inspiring, even a small contribution means a lot — it helps keep this space free, ad-free and growing for everyone.
            </p>
          </div>
          <div className="donation-qr-block flex shrink-0 flex-col items-center gap-[16px]">
            <img
              src="https://home-asset.s3.eu-north-1.amazonaws.com/HariVishnu+Razorpay+QRcode+Donation.png"
              alt="Scan to donate via Razorpay"
              className="donation-qr-img h-[180px] w-[180px] rounded-[12px] border-2 border-[#e5e5e5] object-cover max-[480px]:h-[150px] max-[480px]:w-[150px] min-[481px]:max-[767px]:h-[160px] min-[481px]:max-[767px]:w-[160px] min-[768px]:max-[1024px]:h-[170px] min-[768px]:max-[1024px]:w-[170px]"
            />
            <a
              href="https://razorpay.me/@srinivasanharivishnu"
              target="_blank"
              rel="noopener noreferrer"
              className="donate-btn inline-block cursor-pointer rounded-[10px] border-0 bg-[#fecb2f] px-[24px] py-[12px] text-[1rem] font-[700] text-[#111] no-underline transition-colors duration-200 hover:bg-[#f5bb00]"
            >
              Support the Page →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}